import { Box, Text, Center, Image, VStack, Heading } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { REPORT_RANGES } from "../store/constants";

const organizationLogos = [
  {
    organizationId: "0ASmnwonZfFrOhs6I3De",
    url: "https://cx.storage.averly.com.na/0ASmnwonZfFrOhs6I3De/6d01f853-2c94-4ab0-9360-3b52438f316e.jpg",
  },
  {
    organizationId: "cc229c19-320e-4c4b-9ef0-7e1085e1e49d",
    url: "https://cx.storage.averly.com.na/cc229c19-320e-4c4b-9ef0-7e1085e1e49d/d03ee51a-1c43-4d37-a631-2753eeb8d3e5.png",
  },
  {
    organizationId: "12346b65-a1f9-42c9-bff7-c23768882c02",
    url: "https://cx.storage.averly.com.na/12346b65-a1f9-42c9-bff7-c23768882c02/357b4efe-039c-4f89-ab9a-326bfa9a30cd.jpg",
  },
  {
    organizationId: "f82d60f7-b911-48b4-8f6e-769b72ba4aaf",
    url: "https://cx.storage.averly.com.na/f82d60f7-b911-48b4-8f6e-769b72ba4aaf/8149786f-f7ae-4a22-bd81-046cfd6ed4d8.png",
  },
  {
    organizationId: "D4tJC4RLy7oBWsBTzPGP",
    url: "https://cx.storage.averly.com.na/D4tJC4RLy7oBWsBTzPGP/f654359d-56c7-4118-a96f-08fea1e92522.png",
  },
  {
    organizationId: "01fda133-e3ca-4a1b-ac3e-7af6f986c766",
    url: "https://cx.storage.averly.com.na/01fda133-e3ca-4a1b-ac3e-7af6f986c766/38671345-57a8-4ef5-bff3-87b02bf0af05.jpeg",
  },
  {
    organizationId: "19b8089b-4185-409f-8f9f-77f1969a69f2",
    url: "https://cx.storage.averly.com.na/19b8089b-4185-409f-8f9f-77f1969a69f2/7bb9fb4a-4b84-4e89-9e2e-101221439ddb.png",
  },
];

const getLogoUrl = ({ organizationId, logoData, config }) => {
  const logoRecord = Array.isArray(logoData) ? logoData[0] : logoData;
  return (
    logoRecord?.url ||
    logoRecord?.fileUrl ||
    logoRecord?.publicUrl ||
    logoRecord?.secureUrl ||
    logoRecord?.src ||
    logoRecord?.data?.url ||
    config?.styling?.general?.logoUrl ||
    organizationLogos.find((logo) => logo.organizationId === organizationId)?.url
  );
};

const Cover = () => {
  const { mailingList, logoData, config } = useSelector((state) => state.defaultReducer);

  const {
    serviceName,
    reportRange,
    subscriptionName,
    dateRange,
    userFullName,
    email,
    organizationId,
  } = mailingList || {};

  if (!mailingList || !dateRange || !Array.isArray(dateRange) || dateRange.length < 2) {
    return (
      <Box bg="white" p={6}>
        <Center mt="40px">
          <Text>No mailing list data available</Text>
        </Center>
      </Box>
    );
  }

  const reportRangeStr = {
    day: "Daily",
    week: "Weekly",
    month: "Monthly",
  }[reportRange];

  let [start, end] = [dateRange[0]?.split("-"), dateRange[1]?.split("-")];
  [start, end] = [
    `${start[1]}-${start[0]}-${start[2]}`,
    `${end[1]}-${end[0]}-${end[2]}`,
  ];
  const period =
    reportRange === REPORT_RANGES.DAY
      ? `${dayjs(start, "MM-DD-YYYY").format("MMMM D, YYYY")}`
      : `${dayjs(start, "MM-DD-YYYY").format("MMMM D, YYYY")} to ${dayjs(
          end,
          "MM-DD-YYYY"
        ).format("MMMM D, YYYY")}`;

  const preparedFor = userFullName?.trim().replace(/\s+/g, " ");

  const reportTitle = [reportRangeStr, "Report"].filter(Boolean).join(" ");
  const productLabel = serviceName?.trim() || reportTitle;
  const subscriptionDisplayName = subscriptionName
    ?.trim()
    .replace(/\S+/g, (word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`);
  const logoUrl = getLogoUrl({ organizationId, logoData, config });

  return (
    <Box
      bg="#ffffff"
      color="#111111"
      height="297mm"
      minHeight="297mm"
      position="relative"
      overflow="hidden"
      px="20mm"
      py="27mm"
      fontFamily="Lato, sans-serif"
    >
      <VStack align="stretch" gap={0} height="100%">
        {logoUrl ? (
          <Image
            src={logoUrl}
            maxW="78mm"
            maxH="22mm"
            objectFit="contain"
            objectPosition="left center"
            id="companyLogo"
          />
        ) : (
          <Box height="22mm" />
        )}

        <Box mt="55mm" maxW="156mm" position="relative">
          <Text
            color="#3f3797"
            fontSize="10.5pt"
            fontWeight="800"
            letterSpacing="0"
            lineHeight="1.1"
            mb="8mm"
            textTransform="uppercase"
          >
            {productLabel}
          </Text>
          <Heading
            as="h1"
            color="#111111"
            fontSize="42pt"
            fontWeight="800"
            lineHeight="1"
            letterSpacing="0"
            textAlign="left"
            mb="8mm"
          >
            {reportTitle}
          </Heading>
          {subscriptionDisplayName && (
            <Box mb="4mm">
              <Text color="#6b7280" fontSize="8.5pt" fontWeight="400" lineHeight="1.25" mb="2mm">
                Subscription Name
              </Text>
              <Text color="#111111" fontSize="13pt" fontWeight="700" lineHeight="1.25">
                {subscriptionDisplayName}
              </Text>
            </Box>
          )}
          <Text color="#4b5563" fontSize="15pt" fontWeight="500" lineHeight="1.4" maxW="142mm">
            {period}
          </Text>
        </Box>

        <Box width="100%" height="1.2mm" bg="#45359d" mt="18mm" />

        <Box mt="8mm">
          {(preparedFor || email) && (
            <Box mb="5mm">
              <Text color="#6b7280" fontSize="8.5pt" fontWeight="400" lineHeight="1.2" mb="2mm">
                Prepared By
              </Text>
              {preparedFor && (
                <Text color="#111111" fontSize="11pt" fontWeight="700" lineHeight="1.35">
                  {preparedFor}
                </Text>
              )}
              {email && (
                <Text
                  as="span"
                  color="#4b5563"
                  display="block"
                  fontSize="8.5pt"
                  fontWeight="500"
                  lineHeight="1.35"
                  textDecoration="none"
                >
                  {email}
                </Text>
              )}
            </Box>
          )}

          <Text color="#111111" fontSize="15pt" fontWeight="800" lineHeight="1.25">
            AVERLY DIGITAL SOLUTIONS
          </Text>
        </Box>
        <Box flex="1" />
      </VStack>
    </Box>
  );
};

export default Cover;
